import { authenticatedFetch } from "@lumi/data";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../../auth/AuthContext";
import { createRefreshCallback } from "../../../../auth/refreshCallback";

interface Collection {
  readonly key: string;
  readonly name: string;
  readonly sort: number;
  readonly cardType: string;
  readonly visible: boolean;
}

/**
 * Hook to fetch all collections for admin use
 */
export function useCollections() {
  const { accessToken, refreshAuth } = useAuth();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCollections = useCallback(async () => {
    if (!accessToken) {
      setCollections([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
      const data = await authenticatedFetch<Collection[]>(
        `${baseUrl}/v1/admin/collections`,
        { method: "GET" },
        accessToken,
        createRefreshCallback(refreshAuth),
      );

      setCollections(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch collections";
      setError(message);
      console.error("Error fetching collections:", err);
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshAuth]);

  useEffect(() => {
    void fetchCollections();
  }, [fetchCollections]);

  return { collections, loading, error, refetch: fetchCollections };
}
